import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Paper from 'apollo-react/components/Paper';
import Typography from 'apollo-react/components/Typography';
import Grid from 'apollo-react/components/Grid';
import Box from 'apollo-react/components/Box';
import Button from 'apollo-react/components/Button';
import CircularProgress from 'apollo-react/components/CircularProgress';
import axiosInstance from '../../../axiosInstance';
import ViewOnlyFileDisplay from '../../common/ViewOnlyFileDisplay';
import { useFileDownload } from '../../../hooks/useFileDownload';

const ProductDetailsDetails = ({ persona }) => {
  const { id } = useParams();
  const [productDetails, setProductDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const { downloadFile } = useFileDownload(persona);

  const fetchProductDetails = useCallback(async () => {
    setLoading(true);
    try {
      // determine endpoint based on persona
      const endpoint = persona === 'customer' 
        ? `/api/product-details/enduser/${id}`
        : `/api/product-details/${id}`;
      
      const response = await axiosInstance.get(endpoint);
      setProductDetails(response.data);
    } catch (error) {
      console.error('error fetching product details:', error);
    } finally {
      setLoading(false);
    }
  }, [id, persona]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!productDetails) {
    return (
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        <Typography variant="body1">No Product Details Available.</Typography>
      </Paper>
    );
  }

  const handleDownloadCertificate = async () => {
    if (productDetails?.request?.certificate_file_id) {
      try {
        setDownloadingCertificate(true);
        const fileName = productDetails.request.certificateFile?.original_name || 'Certificate';
        await downloadFile(productDetails.request.certificate_file_id, fileName);
      } finally {
        setDownloadingCertificate(false);
      }
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Paper elevation={1} sx={{ padding: 4, margin: 0 }}>
        {/* header with download certificate button */}
        <Box sx={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h1"
            sx={{ fontWeight: 600 }}
          >
            Product Details
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleDownloadCertificate}
            disabled={!productDetails.request?.certificate_file_id || downloadingCertificate}
          >
            {downloadingCertificate ? 'Downloading...' : 'Download Certificate'}
          </Button>
        </Box>

        {/* request information */}
        {productDetails.request && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="title1"
              component="h2"
              sx={{
                fontWeight: 500,
                marginBottom: 3,
                color: "#333",
                paddingBottom: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              Request Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">License Number</Typography>
                <Typography variant="body1">{productDetails.request.certificate_license_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Reference Number</Typography>
                <Typography variant="body1">{productDetails.request.reference_number || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Typography variant="body1">{productDetails.requestProgressStatus || 'Active'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">Request Name</Typography>
                <Typography variant="body1">{productDetails.request.study_name || '-'}</Typography>
              </Grid>
              {productDetails.request.end_user && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Submitted By</Typography>
                    <Typography variant="body1">{productDetails.request.end_user.username || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="textSecondary">Email</Typography>
                    <Typography variant="body1">{productDetails.request.end_user.email || '-'}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        )}

        {/* product information section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Product Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Proposed Trade Name (English)</Typography>
              <Typography variant="body1">{productDetails.proposed_trade_name_in_english || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Proposed Trade Name (Arabic)</Typography>
              <Typography variant="body1">{productDetails.proposed_trade_name_in_arabic || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">INN / Scientific Name</Typography>
              <Typography variant="body1">{productDetails.inn_scientific_name || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">ATC Code</Typography>
              <Typography variant="body1">{productDetails.atc_code || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* dosage & administration section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Dosage &amp; Administration
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Dosage Form</Typography>
              <Typography variant="body1">{productDetails.dosage_form || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Route Administration</Typography>
              <Typography variant="body1">{productDetails.route_administration || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Concentration</Typography>
              <Typography variant="body1">{productDetails.concentration || '-'}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Concentration Unit</Typography>
              <Typography variant="body1">{productDetails.concentration_unit || '-'}</Typography>
            </Grid>
          </Grid>
        </Box>

        {/* documents section */}
        <Box sx={{ marginBottom: 4 }}>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: "#333",
              paddingBottom: 1,
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            Documents
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
                  Label Artwork (En/Ar)
                </Typography>
                {productDetails.labelArtworkFile ? (
                  <ViewOnlyFileDisplay
                    files={[{
                      id: productDetails.labelArtworkFile.id,
                      file_id: productDetails.labelArtworkFile.id,
                      name: productDetails.labelArtworkFile.original_name,
                      file_name: productDetails.labelArtworkFile.original_name,
                      file_size: productDetails.labelArtworkFile.file_size,
                      mime_type: productDetails.labelArtworkFile.mime_type
                    }]}
                    label=""
                    helperText="download label artwork"
                    persona={persona}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
                  PIL (English)
                </Typography>
                {productDetails.pilEnglishFile ? (
                  <ViewOnlyFileDisplay
                    files={[{
                      id: productDetails.pilEnglishFile.id,
                      file_id: productDetails.pilEnglishFile.id,
                      name: productDetails.pilEnglishFile.original_name,
                      file_name: productDetails.pilEnglishFile.original_name,
                      file_size: productDetails.pilEnglishFile.file_size,
                      mime_type: productDetails.pilEnglishFile.mime_type
                    }]}
                    label=""
                    helperText="download pil (english)"
                    persona={persona}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
                  PIL (Arabic)
                </Typography>
                {productDetails.pilArabicFile ? (
                  <ViewOnlyFileDisplay
                    files={[{
                      id: productDetails.pilArabicFile.id,
                      file_id: productDetails.pilArabicFile.id,
                      name: productDetails.pilArabicFile.original_name,
                      file_name: productDetails.pilArabicFile.original_name,
                      file_size: productDetails.pilArabicFile.file_size,
                      mime_type: productDetails.pilArabicFile.mime_type
                    }]}
                    label=""
                    helperText="download pil (arabic)"
                    persona={persona}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#555', marginBottom: 1 }}>
                  SPC
                </Typography>
                {productDetails.spcFile ? (
                  <ViewOnlyFileDisplay
                    files={[{
                      id: productDetails.spcFile.id,
                      file_id: productDetails.spcFile.id,
                      name: productDetails.spcFile.original_name,
                      file_name: productDetails.spcFile.original_name,
                      file_size: productDetails.spcFile.file_size,
                      mime_type: productDetails.spcFile.mime_type
                    }]}
                    label=""
                    helperText="download spc"
                    persona={persona}
                  />
                ) : (
                  <Typography variant="body1" sx={{ color: '#999', fontStyle: 'italic' }}>
                    Not provided
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* timestamps */}
        <Box sx={{ marginTop: 4, paddingTop: 3, borderTop: "1px solid #e0e0e0" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Registration Date</Typography>
              <Typography variant="body1">{productDetails.registrationDate ? new Date(productDetails.registrationDate).toLocaleString('en-GB') : '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Renewal Date</Typography>
              <Typography variant="body1">{productDetails.renewalDate ? new Date(productDetails.renewalDate).toLocaleString('en-GB') : '-'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Created At</Typography>
              <Typography variant="body1">{new Date(productDetails.createdAt).toLocaleString('en-GB')}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary">Last Updated</Typography>
              <Typography variant="body1">{new Date(productDetails.updatedAt).toLocaleString('en-GB')}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductDetailsDetails;

